import type { ComponentDefinition, Terminal } from "../types";

export const PARALLAX_DC_BOARD_ID = "dist-parallax-088-0001-001";

const feedTerminalIds = Object.freeze(["lug-1", "lug-2", "lug-3"]);
const slotFuseMaxA = Object.freeze(Object.fromEntries(Array.from({ length: 15 }, (_, i) => [`c${i + 1}`, i < 2 ? 30 : 20])));
const feedWireRange = Object.freeze({ smallest: 14, largest: 2 });
const slotWireRange = Object.freeze({ smallest: 16, largest: 12 });
const wireAwgRange = Object.freeze(Object.fromEntries<Readonly<{ smallest: number; largest: number }>>([
  ...feedTerminalIds.map(id => [id, feedWireRange] as const),
  ...Object.keys(slotFuseMaxA).map(id => [id, slotWireRange] as const),
]));
const limits = Object.freeze({ maxTotalA: 100, slotFuseMaxA, feedTerminalIds, wireAwgRange });

/** Exact board marking supplied in David's 2026-10-24 attachment:
 * 088-0001-001, nominal 12 VDC, maximum 100 A, max branch fuse 30 A (1–2),
 * 20 A (3–15); Littelfuse 257 / Bussmann ATC. Accepts stranded copper:
 * feed lugs 2–14 AWG, branch terminals 12–16 AWG. Source image and conflict
 * with the earlier 85 A/20 A 5300 assembly label: docs/parallax-5355.md.
 *
 * Three positive lugs are common and UNFUSED, confirmed by 5300 Rev B p8.
 * This board does not contain the separately mounted negative terminal bar.
 * Fuse ceilings and the aggregate board limit are not terminal ampacities.
 * A lower power-center assembly label still governs the installed assembly. */
const board: ComponentDefinition = {
  id: PARALLAX_DC_BOARD_ID,
  name: "Parallax 088-0001-001 DC Fuse Board · 15 Circuits",
  shortName: "088-0001-001 · 15 circuits",
  subtitle: "12V positive-only board · 100A marked maximum",
  manufacturer: "Parallax Power Supply",
  model: "088-0001-001",
  category: "distribution",
  isFuseBlock: true,
  fuseSlotType: "ATC",
  equipmentLimitExemption: "fuse",
  compatibleVoltages: [12],
  width: 300,
  height: 264,
  terminals: [
    ...feedTerminalIds.map((id, i): Terminal => ({ id, label: `LUG ${i + 1} +`, maxAmp: 0,
      polarity: "positive", direction: "input", side: "top", offset: .15 + i * .35 })),
    ...Array.from({ length: 15 }, (_, i): Terminal => ({ id: `c${i + 1}`, label: `FUSE ${i + 1}`,
      maxAmp: 0, polarity: "positive", direction: "output", side: i < 8 ? "left" : "right",
      offset: i < 8 ? (i + 1) / 9 : (i - 7) / 8 })),
  ],
  // Keep the unfused rail reachable even when every fused-slot edge is cut.
  internalDcConnections: [["lug-1", "lug-2"], ["lug-2", "lug-3"]],
};

function electricalShape(def: ComponentDefinition): string {
  return JSON.stringify({ category: def.category, manufacturer: def.manufacturer, model: def.model,
    voltage: def.voltage, compatibleVoltages: def.compatibleVoltages, acVoltage: def.acVoltage,
    ampDraw: def.ampDraw, ampSurge: def.ampSurge, ampOutput: def.ampOutput, renderVariant: def.renderVariant,
    inputVoltage: def.inputVoltage, outputVoltage: def.outputVoltage, conversionEfficiency: def.conversionEfficiency,
    maxInputA: def.maxInputA, outputCurrentLimitsA: def.outputCurrentLimitsA,
    loadCurrentModel: def.loadCurrentModel, watts: def.watts,
    dcBreaker: def.dcBreaker, bms: def.bms, currentShunt: def.currentShunt, settings: def.settings,
    isFuseBlock: def.isFuseBlock, fuseSlotType: def.fuseSlotType, fuseBusGroups: def.fuseBusGroups,
    internalDcConnections: def.internalDcConnections, oneWayDcConnections: def.oneWayDcConnections,
    simulateStates: def.simulateStates,
    terminals: def.terminals.map(({ id, maxAmp, polarity, direction }) => ({ id, maxAmp, polarity, direction })),
  });
}
const boardShape = electricalShape(board);

/** Manufacturer limits apply only to this documented electrical identity.
 * A retained catalog ID after electrical customization does not prove it. */
export function parallaxDcBoardLimits(def: ComponentDefinition): typeof limits | undefined {
  return def.id === PARALLAX_DC_BOARD_ID && electricalShape(def) === boardShape ? limits : undefined;
}

export const PARALLAX: ComponentDefinition[] = [board];
